import { Location } from '@angular/common';
import { Input, Component, OnInit, AfterViewInit, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Company } from '@models/company.model';
import { User, PurchaseOrderDto } from '@app/models';
import { Workflow, TaskDefinition, DateMeta, WorkflowInstance, TaskInstance } from '@models/workflow.model';
import { NgbPopoverConfig, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from '@services/company.service';
import { Toaster } from '@services/toaster.service';
import { LocalStorage } from 'ngx-webstorage';
import * as moment from 'moment';
import { TaskCreationModalComponent } from '@app/modules/workflows/modals/task-creation-modal/task-creation-modal.component';
import { TaskInstanceModalComponent } from '@app/modules/workflows/modals/task-instance-modal/task-instance-modal.component';
import { ApplyWorkflowModalComponent } from '@app/modules/workflows/modals/apply-workflow-modal/apply-workflow-modal.component';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

@Component({
  selector: 'ngx-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnInit, AfterViewInit {
  readonly FILE_LIMIT: number = 25000000;

  @LocalStorage() organizationId: string;
  @LocalStorage() role: string;
  @LocalStorage() companyType: string;

  @Input() workflowDefinition: Workflow;  
  @Input() po: PurchaseOrderDto;
  @Input() reloadWorkflowInstanceEvent: EventEmitter<any>;

  _workflowInstance: WorkflowInstance;
  get workflowInstance(): WorkflowInstance {
      return this._workflowInstance;
  }
  @Input() set workflowInstance(value: WorkflowInstance) {
      this._workflowInstance = value;
      this.tasks = value.tasks;
  }

  tasksChangedEvent: EventEmitter<any> = new EventEmitter<any>();
  tasks: any[] = [];
  mostRelevantTask: any[] = [];
  ctaFieldValues: any = {};

  loading: boolean = false;
  isDualViewEnabled: boolean = false;
  focusedTaskIndex: number;

  readonly startPosition = 30;
  currentPosition = this.startPosition;
  positionJump = 185*2;

  user$: Observable<User>;
  currentUser: User;

  constructor(
    config: NgbPopoverConfig,
    private $company: CompanyService,
    private $modal: NgbModal,
    private activatedRoute: ActivatedRoute,
    private $toaster: Toaster,
    public location: Location,
    private store: Store<{ user: User }>,
    public router: Router,
  ) {
    config.placement = 'left';
    config.triggers = 'hover';
  }

  async ngOnInit() {
    this.tasksChangedEvent.subscribe(e => {
      this.getTasks();
    })
    
    if (this.workflowDefinition) {
      this.getTasks();
    } else if (this.workflowInstance) {
      this.tasks = this.workflowInstance.tasks;
    }

    this.user$ = this.store.select('user');
    this.user$
      .subscribe((user) => {
        this.currentUser = (user as User);
      });
  }

  ngAfterViewInit() {
    if (this.workflowInstance) {
      this.setMostRelevantTask();
    }
    if (this.reloadWorkflowInstanceEvent) {
      this.reloadWorkflowInstanceEvent.subscribe(v => {
        this.setMostRelevantTask();
        /*if (v.open_task_id) {
          this.openTaskInstanceModal(this.getTaskInstance(v.open_task_id));
        }*/
      });
    }
  }

  getTasks() {
    this.loading = true;
    
    let req: any;

    if (this.workflowDefinition) {
      req = this.$company.workflowTaskDefinitions(this.organizationId, this.workflowDefinition.id);
    } else if (this.workflowInstance) {
      req = this.$company.workflowTaskInstances(this.workflowInstance.id, this.workflowInstance.instance_id);
    }

    req.subscribe((result) => {
      this.tasks = result;
      if (this.workflowInstance) {
        this.setMostRelevantTask();
      }
      this.loading = false;
    }, (error) => {
      this.loading = false;
    });
  }

  setMostRelevantTask() {
    let task: any;
    task = this.tasks.find(t => t.active === true);
    if (!task) {
      task = this.tasks.find(t => t.completed_by === null && t.allow_anyone_to_complete === true);
    }
    if (!task) {
      task = this.tasks[this.tasks.length - 1];
    }
    this.mostRelevantTask = ([] as any[]);
    this.mostRelevantTask.push(task);

    if (this.workflowInstance) {
      this.jumpToIndex(task.order);
    }
  }

  navMove($event){
    let dir = (event.target as any)?.className.indexOf('right');
    this.currentPosition += dir > 0 ? this.positionJump * -1 : this.positionJump;
    this.focusedTaskIndex = null;
    this.clearFocusedTasks();
  }

  jumpToIndex(index: number) {
    if (index === null) {
      return;
    }

    if (index < 0) {
      index = 0;
    }

    this.focusedTaskIndex = index;
    if (index > this.tasks.length) {
      index = this.tasks.length - 1;
    }

    if (index > 5) {
      // leaving space to the left
      index = index - 2;
    }

    if (this.isDualViewEnabled) {
      index = Math.floor(index / 2);
    }

    let pos = this.startPosition + (this.positionJump * (-1) * index);

    if (pos > this.startPosition) {
      pos = this.startPosition;
    }

    this.currentPosition = pos;

    this.clearFocusedTasks();
    document.getElementById("t-" + this.focusedTaskIndex).classList.add("focused");
  }

  clearFocusedTasks() {
    var elems = document.querySelectorAll(".task.focused");
    [].forEach.call(elems, function(el) {
        if (!el.classList.contains("border-primary")) {
          el.classList.remove("focused");
        }
    });
  }

  openNewTaskDefinitionModal() {
    const modalRef = this.$modal.open(TaskCreationModalComponent, {
      size: 'lg',
      container: 'nb-layout',
    });
    modalRef.componentInstance.organizationId = this.organizationId;
    modalRef.componentInstance.workflow = this.workflowDefinition;
    modalRef.componentInstance.index = this.tasks.length;
    modalRef.componentInstance.maxIndex = this.tasks.length;
    modalRef.componentInstance.tasks = this.tasks;
    modalRef.result.then((result) => {
      this.tasksChangedEvent.emit();
    }, (error) => {
      console.error(error);
    });
  }

  openTaskModal(task, index) {
    if (this.workflowDefinition) {
      this.openTaskDefinitionModal(task, index);
    }
    if (this.workflowInstance) {
      this.openTaskInstanceModal(task);
    }
  }

  openTaskDefinitionModal(task, index) {
    const modalRef = this.$modal.open(TaskCreationModalComponent, {
      size: 'lg',
      container: 'nb-layout',
    });
    modalRef.componentInstance.organizationId = this.organizationId;
    modalRef.componentInstance.workflow = this.workflowDefinition;
    modalRef.componentInstance.index = index + 1;
    modalRef.componentInstance.maxIndex = this.tasks.length;
    modalRef.componentInstance.tasks = this.tasks;
    modalRef.componentInstance.task = task;
    modalRef.result.then((result) => {
      this.tasksChangedEvent.emit();
    }, (error) => {
      console.error(error);
    });
  }

  openTaskInstanceModal(task, approvalTask = null) {
    const modalRef = this.$modal.open(TaskInstanceModalComponent, {
      size: 'lg',
      container: 'nb-layout',
    });
    modalRef.componentInstance.organizationId = this.organizationId;
    modalRef.componentInstance.workflow = this.workflowInstance;
    modalRef.componentInstance.tasks = this.tasks;
    modalRef.componentInstance.po = this.po;
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.approvalTask = approvalTask;
    modalRef.componentInstance.reloadWorkflowInstanceEvent = this.reloadWorkflowInstanceEvent;
    modalRef.result.then((result) => {
      this.tasksChangedEvent.emit();
    }, (error) => {
      console.error(error);
    });
  }

  openApplyWorkflowModal() {
    const modalRef = this.$modal.open(ApplyWorkflowModalComponent, {
      size: 'xl',
      container: 'nb-layout',
    });
    modalRef.componentInstance.organizationId = this.organizationId;
    modalRef.componentInstance.workflow = this.workflowDefinition;
    modalRef.componentInstance.tasks = this.tasks;
    modalRef.result.then((result) => {
      this.tasksChangedEvent.emit();
    }, (error) => {
      console.error(error);
    });
  }

  getRelativeDateString(dateMeta) {
    return new DateMeta(dateMeta).GetRelativeDateString();
  }

  isLastTaskVisible() {
    if (this.currentPosition >= this.startPosition) {
      return false;
    }
    let i: number = (Math.abs(this.currentPosition) + this.startPosition) / (this.positionJump/2);
    let max: number = this.isDualViewEnabled ? Math.ceil(this.tasks.length / 2) : this.tasks.length;
    if (i >=  max) {
      return true;
    }
    return false;
  }

  toggleDualView() {
    this.isDualViewEnabled = !this.isDualViewEnabled;
    this.jumpToIndex(this.focusedTaskIndex);
  }

  isTextOverflow(elementId: string): boolean {
    const elem = document.getElementById(elementId);
    if (elem) {
      return (elem.offsetWidth < elem.scrollWidth);
    } else {
      return false;
    }
  }

  isTaskExpired(task) {
    if ((this.workflowDefinition && task.due_date_meta.specific_date === null) || !task.due_date) {
      return false;
    }
    if (!!task.completed_by) {
      return false;
    }

    let date = task.due_date || task.due_date_meta.specific_date;
    let now = moment();
    if (moment.utc(date).isBefore(now)) {
      return true;
    }

    return false;
  }

  getCtaButtonTooltip(task) {
    if (!this.currentUser) {
      return '';
    }
    if (task.active) {
      return task.task_type.action_instructions.concat(': ', task.entity.name);
    }
    if (task.is_dependency_completed === false && task.depends_on_task_instance_id !== null) {
      return 'You cannot complete this task because the task upon which it depends has not been completed.'
    }
    if (
        task.allow_anyone_to_complete === false &&
        (task.responsible.id !== this.currentUser.id &&
        task.responsible.organization_id !== this.currentUser.organization_id)
    ) {
      return 'You cannot complete this task because you are not the assigned responsible nor from the same organization.'
    }
    if (!task.active) {
      return 'You cannot complete this task.'
    }
    return task.task_type.action_instructions.concat(': ', task.entity.name);
  }

  handleCtaButtonClick($event, task: TaskInstance, index: number, isDone: boolean = null) {
    $event.stopPropagation();

    let payload: any = {};

    switch(task.task_type.interaction_type) {
      case 'simple':
        payload.is_done = isDone;
        break;
      case 'submit':
        payload.input = this.ctaFieldValues['entity-value-' + index];
        break;
      case 'approve':
        this.openTaskInstanceModal(this.getTaskInstance(task.affected_task_instance_id), task);
        return;
      case 'upload':
        document.getElementById('file-input-' + index).click();
        return;
    }

    this.$company.completeTaskInstance(
      this.workflowInstance.id,
      this.workflowInstance.instance_id,
      task.id,
      task.instance_id,
      payload,
    ).subscribe(res => {
      this.reloadWorkflowInstanceEvent.emit(res);
      this.$toaster.show('success', 'Success', 'You completed the task.');
    }, error => {
      console.error(error);
      this.$toaster.show('error', 'An error occurred', 'Please try again later.');
    });

  }

  completeTaskWithFile($event, task) {
    $event.stopPropagation();

    const fileList: FileList = $event.srcElement.files;
    const formData: FormData = new FormData();

    if (fileList.length === 0) {
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (file.size > this.FILE_LIMIT) {
        this.$toaster.show('warning', 'File size', 'Maximum file size for uploads is 25MB');
        return;
      }
      formData.append('file', file, file.name);
    }

    this.$company.completeTaskInstanceWithFile(
      this.workflowInstance.id,
      this.workflowInstance.instance_id,
      task.id,
      task.instance_id,
      formData,
    ).subscribe(res => {
      this.reloadWorkflowInstanceEvent.emit(res);
      this.$toaster.show('success', 'Success', 'You completed the task.');
    }, error => {
      console.error(error);
      this.$toaster.show('error', 'An error occurred', 'Please try again later.');
    });
  }

  showTimelineOptions() {
    console.log('showTimelineOptions');
  }

  deleteTimeline() {
    console.log('deleteTimeline');
  }

  openHelpModal() {
    console.log('openHelpModal');
  }

  getTaskInstance(taskId) {
    return this.tasks.find(t => t.instance_id === taskId);
  }

  areAllTasksDisabled() {
    let t = this.tasks.find(t => t.is_enabled === true);
    return !t;
  }

  getUserTimezone() {
    let tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? tz : 'utc';
  }
}
